import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import {
  ActivitiesDataScope,
  getStoreScope,
  TrackingActivitiesSelectors,
} from '@core/activities-and-notifications-data';
import type { TrackingActivity } from '@core/activities-types';
import { filterBoolean } from '@core/common-legacy';
import type { Requisition } from '@core/requisitions-types';

import { RootPageDataService } from './root-page-data.service';

@Injectable()
export class ActivityPageDataService {
  private readonly rootData = inject(RootPageDataService);
  private readonly store = inject(Store);

  public requisition$: Observable<Requisition>;
  public activities$: Observable<TrackingActivity[]>;
  public manageCandidateAbility$: Observable<boolean>;
  public isLoaded$: Observable<boolean>;
  public isLoading$: Observable<boolean>;

  constructor() {
    const rootData = this.rootData;
    const store = this.store;

    this.requisition$ = rootData.requisition$;
    const requisitionId$ = this.requisition$.pipe(map(requisition => requisition.id));
    this.activities$ = requisitionId$.pipe(
      switchMap(id => {
        const requisitionActivitiesScope = getStoreScope(ActivitiesDataScope.Requisition, id);
        return store
          .select(
            TrackingActivitiesSelectors.entities({
              scope: requisitionActivitiesScope,
            }),
          )
          .pipe(filterBoolean);
      }),
    );
    this.manageCandidateAbility$ = store.select(
      AbilitiesSelectors.hasOne(Ability.ManageCandidates),
    );
    this.isLoaded$ = requisitionId$.pipe(
      switchMap(id => store.select(TrackingActivitiesSelectors.requisitionScopeIsLoaded(id))),
    );
    this.isLoading$ = requisitionId$.pipe(
      switchMap(id => store.select(TrackingActivitiesSelectors.requisitionScopeIsPending(id))),
    );
  }
}
